import type { TFunction } from 'i18next'
import { AbsenceApproval, AbsenceReason } from '@/api/model'

/**
 * Translated label of an absence reason (NS `service-modules`,
 * `absence.reason.<Enum>`). Falls back to the raw value for an unknown reason.
 */
export function getAbsenceReasonLabel(t: TFunction, reason: string): string {
  const key = `absence.reason.${reason}`
  const label = t(key)
  return label === key ? reason : label
}

/** Translated label of the approval status (`absence.status.<Enum>`). */
export function getAbsenceStatusLabel(t: TFunction, status: AbsenceApproval): string {
  return t(`absence.status.${status}`)
}

/**
 * Fixed color per absence reason (for the calendar + its legend). A stable
 * reason→color mapping so the same reason looks identical across months/renders.
 */
export const ABSENCE_REASON_COLOR: Record<AbsenceReason, string> = {
  [AbsenceReason.Vacation]: '#2563eb',
  [AbsenceReason.VacationHalfDay]: '#60a5fa',
  [AbsenceReason.Illness]: '#dc2626',
  [AbsenceReason.IllnessWithoutDoctorsNotice]: '#f87171',
  [AbsenceReason.ChildSick]: '#ea580c',
  [AbsenceReason.SpecialVacation]: '#16a34a',
  [AbsenceReason.SpecialVacationHalfDay]: '#4ade80',
  [AbsenceReason.CompensatoryTimeOff]: '#0891b2',
  [AbsenceReason.ParentalLeave]: '#7c3aed',
  [AbsenceReason.MaternityProtection]: '#db2777',
  [AbsenceReason.Exemption]: '#65a30d',
}

/** Calendar color of a reason; fallback (gray) for unknown raw values. */
export function getAbsenceReasonColor(reason: string): string {
  return ABSENCE_REASON_COLOR[reason as AbsenceReason] ?? '#64748b'
}

/** Color classes of the status badge (traffic light: pending/approved/rejected). */
export const ABSENCE_STATUS_CLASS: Record<AbsenceApproval, string> = {
  [AbsenceApproval.Pending]: 'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  [AbsenceApproval.Approved]: 'border-transparent bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300',
  [AbsenceApproval.Rejected]: 'border-transparent bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
}
